/**
 * The lib's switches as the server set them in `window.eflib`, from config/app_eflib.php. See
 * lib/docs/extensions.md and lib/docs/features.md.
 *
 *     if (hasExtension('notes')) ...
 *     if (extensionOption('announcements', 'team')) ...
 *     if (hasFeature('webhooks')) ...
 */

/** Every extension of the lib, one per manifest `extensions/<name>/extension.php` - a unit test keeps them in step. */
export const EFLIB_EXTENSIONS = [
	'announcements',
	'calendar',
	'context',
	'events',
	'letterhead',
	'map',
	'mcp',
	'notes',
	'teams',
	'text_templates',
	'user_location',
	'widgets',
] as const;

export type EflibExtension = (typeof EFLIB_EXTENSIONS)[number];

/** Every feature of Lib\Support\Features::CATALOG. */
export type EflibFeature =
	| 'webhooks'
	| 'self_registration'
	| 'mail_theme_overrides'
	| 'letterhead_overrides'
	| 'text_templates_overrides'
	| 'metrics';

/** What window.eflib carries: the extensions that are on with their options, and every feature. */
export type EflibState = {
	extensions: Partial<Record<EflibExtension, Record<string, boolean>>>;
	features: Partial<Record<EflibFeature, boolean>>;
};

function state(): EflibState {
	return globalThis.eflib ?? { extensions: {}, features: {} };
}

/** Whether the app has the extension. */
export function hasExtension(name: EflibExtension): boolean {
	return state().extensions[name] !== undefined;
}

/** An option of an extension, false while the extension is off. */
export function extensionOption(name: EflibExtension, option: string): boolean {
	return state().extensions[name]?.[option] === true;
}

/** Whether a feature is on in this deployment. */
export function hasFeature(name: EflibFeature): boolean {
	return state().features[name] === true;
}
